import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  signal,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialog, MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  HLM_DIALOG_SHELL,
  HlmAlertComponent,
  HlmBtnDirective,
  HlmIconComponent,
  HlmInputDirective,
  HlmNativeSelectDirective,
  HlmSpinnerComponent,
  ToastService,
} from '../../../../ui';
import { PermissionService } from '../../../../core/auth/permission.service';
import { ConfirmDialogComponent } from '../../../../shared/confirm-dialog/confirm-dialog.component';
import { SubBrand, SubBrandPayload } from '../../domains/domain.model';
import { SubBrandService } from '../sub-brand.service';
import { BrandLogoComponent } from '../../brand-logo.component';
import { mapHttpError } from '../../brand-error.util';

export interface SubBrandFormDialogData {
  existing: SubBrand | null;
  /** `domain` is the parent brand's domain — resolves the sub-brand logo preview. */
  brands: { id: number; name: string; domain?: string }[];
  priceLists: { id: number; name: string }[];
}

const mapError = (err: unknown): string =>
  mapHttpError(err, {
    400: 'Fill in name, description and parent brand.',
    404: 'Parent brand or price list no longer exists.',
  });

@Component({
  selector: 'cmt-sub-brand-form-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    ...HLM_DIALOG_SHELL,
    HlmBtnDirective,
    HlmIconComponent,
    HlmInputDirective,
    HlmNativeSelectDirective,
    HlmSpinnerComponent,
    HlmAlertComponent,
    BrandLogoComponent,
  ],
  templateUrl: './sub-brand-form-dialog.component.html',
})
export class SubBrandFormDialogComponent {
  readonly data = inject<SubBrandFormDialogData>(MAT_DIALOG_DATA);
  private readonly dialogRef = inject(MatDialogRef<SubBrandFormDialogComponent>);
  private readonly subBrandService = inject(SubBrandService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialog = inject(MatDialog);
  private readonly toastService = inject(ToastService);
  private readonly permissionService = inject(PermissionService);

  /** Write permission gates both Save and the new Delete action. */
  protected readonly canWrite = computed(() => this.permissionService.canWrite('brandManager'));

  readonly saving = signal(false);
  readonly deleting = signal(false);
  readonly errorMessage = signal<string | null>(null);

  readonly form = inject(FormBuilder).nonNullable.group({
    name: [this.data.existing?.name ?? '', Validators.required],
    parentBrand: [this.data.existing?.parentBrandId ?? null as number | null, Validators.required],
    description: [this.data.existing?.description ?? '', Validators.required],
    defaultPriceListId: [this.data.existing?.defaultPriceListId ?? null as number | null],
    defaultNgLimit: [this.data.existing?.defaultNgLimit ?? null as number | null],
  });

  // --- Live logo preview: parent brand's domain + the sub-brand name ---
  private readonly parentBrandValue = toSignal(this.form.controls.parentBrand.valueChanges, {
    initialValue: this.form.controls.parentBrand.value,
  });
  private readonly nameValue = toSignal(this.form.controls.name.valueChanges, {
    initialValue: this.form.controls.name.value,
  });
  /** Parent brand's domain — resolves the sub-brand logo asset folder. */
  protected readonly parentDomain = computed(() => {
    const id = this.parentBrandValue();
    if (id == null) return null;
    return this.data.brands.find(b => b.id === id)?.domain ?? null;
  });
  /** Sub-brand name drives the logo sub-folder (lowercased downstream). */
  protected readonly previewName = computed(() => this.nameValue().trim());

  submit(): void {
    // Guard against double-submit (Enter key races the [disabled] flush) and
    // against a read-only user reaching the mutation via a stale/forgotten opener.
    if (this.saving() || !this.canWrite()) return;
    this.errorMessage.set(null);

    const nameVal = this.form.controls.name.value.trim();
    if (!nameVal) {
      this.form.controls.name.setErrors({ required: true });
      return;
    }

    const descVal = this.form.controls.description.value.trim();
    if (!descVal) {
      this.form.controls.description.setErrors({ required: true });
      return;
    }

    if (this.form.controls.parentBrand.value == null) {
      this.form.controls.parentBrand.markAsTouched();
      this.form.controls.parentBrand.setErrors({ required: true });
      return;
    }

    const priceListRaw = this.form.controls.defaultPriceListId.value;
    const ngLimitRaw = this.form.controls.defaultNgLimit.value;

    const payload: SubBrandPayload = {
      name: nameVal,
      description: descVal,
      parentBrandId: this.form.controls.parentBrand.value as number,
      defaultPriceListId: priceListRaw != null && String(priceListRaw).trim() !== '' ? Number(priceListRaw) : null,
      defaultNgLimit: ngLimitRaw != null && String(ngLimitRaw).trim() !== '' ? Number(ngLimitRaw) : null,
    };

    this.saving.set(true);

    const request$ = this.data.existing != null
      ? this.subBrandService.updateSubBrand(this.data.existing.id, payload)
      : this.subBrandService.createSubBrand(payload);

    request$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => this.dialogRef.close(true),
      error: (err: unknown) => {
        this.saving.set(false);
        this.errorMessage.set(mapError(err));
      },
    });
  }

  /**
   * Delete is only available in edit mode (existing != null). Confirms via the
   * shared dialog, then deletes; a 409/500 means the sub-brand is still
   * referenced and the backend refused the delete.
   */
  protected confirmDelete(): void {
    if (!this.canWrite() || this.data.existing == null) return;
    const existing = this.data.existing;
    this.dialog
      .open(ConfirmDialogComponent, {
        data: {
          title: 'Delete sub-brand?',
          message: `"${existing.name}" will be permanently removed. This fails if customers or domains still reference this sub-brand.`,
          confirmLabel: 'Delete',
          cancelLabel: 'Cancel',
          confirmColor: 'warn',
          icon: 'warning',
        },
        width: '460px',
        maxWidth: '92vw',
        autoFocus: false,
        panelClass: 'cmt-dialog-panel',
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(ok => {
        if (ok) this.delete(existing.id);
      });
  }

  private delete(id: number): void {
    this.errorMessage.set(null);
    this.deleting.set(true);
    this.subBrandService
      .deleteSubBrand(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.toastService.open('Sub-brand deleted', 'OK', { duration: 3000 });
          this.dialogRef.close(true);
        },
        error: (err: unknown) => {
          this.deleting.set(false);
          const referenced = 'Sub-brand is still referenced — remove those references first.';
          this.errorMessage.set(
            mapHttpError(err, { 409: referenced, 500: referenced }, 'Delete failed — please retry.'),
          );
        },
      });
  }

  protected cancel(): void {
    this.dialogRef.close(false);
  }
}
